import i18next from 'i18next';
import React, { useCallback, useMemo } from 'react';

import { AgencyOrgIcon, CustomerOrgIcon, WhiteLabelOrgIcon } from '~/assets/organization';
import { Organization, OrganizationPageQueryOrganization, User } from '~/constants/graphqlTypes';
import { CurrentOrg } from '~/contexts/CurrentOrgContext';
import {
  Organization as FullOrganization,
  OrganizationQuery,
  OrganizationType,
  Role,
  useListOrganizationRolesQuery,
} from '~/types/api.graphql';

import { getRoleTitle } from './User';

export const mapOrgTypeIcons = {
  [OrganizationType.WhiteLabel]: <WhiteLabelOrgIcon />,
  [OrganizationType.Agency]: <AgencyOrgIcon />,
  [OrganizationType.Customer]: <CustomerOrgIcon />,
};

// The tree of org types reachable below (and including) each org type, used to scope the
// sidebar's org menu items to what's visible under the current global org selection.
const orgTypesTree: Record<OrganizationType, OrganizationType[]> = {
  [OrganizationType.WhiteLabel]: [OrganizationType.WhiteLabel, OrganizationType.Agency, OrganizationType.Customer],
  [OrganizationType.Agency]: [OrganizationType.Agency, OrganizationType.Customer],
  [OrganizationType.Customer]: [OrganizationType.Customer],
};

export const getSidebarOrgTypes = (
  activeOrg: CurrentOrg | null,
  orgMenuItemTypes: OrganizationType[],
): OrganizationType[] => {
  if (!activeOrg) return orgMenuItemTypes;

  const candidateTypes = orgTypesTree[activeOrg.organizationType] || [];
  return candidateTypes.filter((type) => orgMenuItemTypes.includes(type));
};

export const getOrganizationInitials = (organizationName: string) => {
  const words = organizationName.split(' ');
  const firstWord = words[0];
  const secondWord = words[1];
  const firstLetter = firstWord[0] || '';
  const secondLetter = secondWord ? secondWord[0] : firstWord[1] || '';
  return `${firstLetter}${secondLetter}`.toUpperCase();
};

export interface OrganizationAddress {
  addressLine1?: string;
  address?: string;
  city?: string;
  state?: string;
}

export type ExtendedOrganization = FullOrganization & { __typename?: string };

export const getOrganizationAddress = (organization: OrganizationAddress) => {
  return [organization?.addressLine1 || organization?.address, organization?.city, organization?.state]
    .map((component) => component?.trim())
    .filter((component) => !!component)
    .join(', ');
};

export const validateOrgPlan = async (organization: Organization, getSubscriptionInfo) => {
  if (organization?.subscriptionOverride) return true;
  if (!organization?.paidSubscription) return false;
  try {
    const result = await getSubscriptionInfo();
    return !!result?.data?.subscriptionInfo;
  } catch (error) {
    return false;
  }
};

export const isB2BOrg = (organization: Organization) => organization?.businessDataEnabled;
export const isFreeOrg = (organization: Organization) =>
  !(organization.paidSubscription || organization.subscriptionOverride);

export const isEmailValidationEnabled = (
  organization: Pick<OrganizationPageQueryOrganization, 'disableEmailValidationOwn' | 'disableEmailValidationFinal'>,
): boolean => {
  if (!organization) return true; // Default to enabled if org is not available

  // Get the organization's own value (retrieveFinalValue: false)
  const { disableEmailValidationOwn } = organization;

  // If the org has its own value (not null), use it
  if (typeof disableEmailValidationOwn === 'boolean') {
    return !disableEmailValidationOwn;
  }
  // If it's null, it means inheriting from parent, so use the final value (retrieveFinalValue: true)
  const { disableEmailValidationFinal } = organization;
  return !disableEmailValidationFinal;
};

export const getOrganizationRole = (organization: OrganizationPageQueryOrganization, me: User) => {
  const userRole = me?.userRoles?.find((role) => role?.organizationId === organization?.id);
  if (!userRole) return i18next.t('organizations.notMember');
  return getRoleTitle(userRole?.assignedRole?.name);
};

// Org fetched by queries/organization.graphql, whose parentOrganization chain (2 levels deep) is
// used to walk up the hierarchy (max depth: WhiteLabel -> Agency -> Customer, 3 levels total).
type OrgWithAncestorChain = NonNullable<OrganizationQuery['organization']>;

export const getOrgAncestorChain = (org: OrgWithAncestorChain): string[] => {
  const parent = org.parentOrganization;
  const grandparentId = parent && 'parentOrganization' in parent ? parent.parentOrganization?.id : null;
  return [org.id, parent?.id, grandparentId].filter((id): id is string => !!id);
};

// An org is "within scope" of activeOrg when activeOrg is its ancestor (or itself). A null
// activeOrgId means "All organizations" is selected, which is compatible with any org.
export const isOrgWithinActiveScope = (activeOrgId: string | null, org: OrgWithAncestorChain): boolean => {
  if (!activeOrgId) return true;
  return getOrgAncestorChain(org).includes(activeOrgId);
};

export const useRoles = (orgId: string) => {
  const { data, loading } = useListOrganizationRolesQuery({
    variables: {
      organizationId: orgId,
    },
    skip: !orgId,
  });

  const isRoleDuplicated = useCallback(
    (role: Role) => data?.listOrganizationRoles?.some((r) => r.name === role.name && r.id !== role.id),
    [data],
  );

  const roles = useMemo(
    () =>
      data?.listOrganizationRoles?.map((role) => ({
        ...role,
        name: isRoleDuplicated(role as Role) ? `${role.name} (${role.organization?.name})` : role.name,
      })) || [],
    [data],
  );

  return { roles, loading };
};
